<template>
	<div class="cassie-vertical-md mt-1">
		<FilterBar
			:search-query.sync="cookieCategorySearchQuery"
			search-query-label="Service Category Name"
			:selected-brand-id.sync="selectedAdminPortalBrandId"
			@persistSearchQuery="changeCookieCategorySearchQuery"
			@persistSelectedBrandId="changeAdminPortalBrandFilter"
			@clearFilters="clearFilters"
		>
			<template #after-filters>
				<Dropdown
					v-model="selectedCookieCategoryType"
					label="Service Category Type"
					:items="categoryTypeFilterItems"
					custom-sort
					@change="changeCookieCategoryType"
				/>
			</template>
			<template #action>
				<PrimaryActionButton
					v-if="userCanCreateUpdate"
					@click="showManageModal = true"
				>
					<v-icon left>
						mdi-plus
					</v-icon>
					Create
				</PrimaryActionButton>
			</template>
		</FilterBar>
		<SectionCard>
			<template #title>
				Service Categories
			</template>
			<template #body>
				<DataTable
					:items="filteredCategories"
					:headers="tableHeaders"
					sort-by="id"
					:sort-desc="true"
					@click:row="cookieCategoryToEdit = $event"
				>
					<template #item.categoryName="{ item }">
						{{ item.categoryName }}
						<Chip
							v-if="item.channelGuid"
							class="ml-2 prussian-blue white--text rounded-pill"
						>
							Cassie Default
						</Chip>
					</template>
					<template #item.type="{ item }">
						{{ item.isStrictlyCategory ? 'Strictly Necessary' : 'User Consent' }}
					</template>
					<template #item.usage="{ item }">
						{{ returnUsageCount(item) }}
					</template>
					<template #item.action="{ item }">
						<v-menu
							offset-y
							left
						>
							<template #activator="{ on, attrs }">
								<v-btn
									icon
									small
									v-bind="attrs"
									v-on="on"
									@click.stop
								>
									<v-icon>
										mdi-dots-horizontal
									</v-icon>
								</v-btn>
							</template>
							<v-list dense>
								<v-list-item
									v-if="userCanCreateUpdate
										&& userHasBrand(item.brandId)
										&& !isSystemCategory(item)"
									@click="cookieCategoryToEdit = item"
								>
									<v-list-item-content>
										<v-list-item-title>
											Edit
										</v-list-item-title>
									</v-list-item-content>
								</v-list-item>
								<v-list-item
									v-else
									@click="cookieCategoryToEdit = item"
								>
									<v-list-item-content>
										<v-list-item-title>
											View
										</v-list-item-title>
									</v-list-item-content>
								</v-list-item>
								<v-list-item
									v-if="canDeleteCategory(item)"
									@click.stop.prevent="categoryToRemove = item"
								>
									<v-list-item-content>
										<v-list-item-title>
											Delete
										</v-list-item-title>
									</v-list-item-content>
								</v-list-item>
							</v-list>
						</v-menu>
					</template>
				</DataTable>
			</template>
		</SectionCard>
		<CookieCategoryModal
			v-if="showManageModal || cookieCategoryToEdit"
			:cookie-category-to-edit="cookieCategoryToEdit"
			:user-full-permissions="
				cookieCategoryToEdit
					? (userCanCreateUpdate
						&& userHasBrand(cookieCategoryToEdit.brandId)
						&& !isSystemCategory(cookieCategoryToEdit))
					: userCanCreateUpdate"
			@close="closeModal"
			@submit="submitCookieCategory"
		/>
		<ConfirmDeleteModal
			v-if="categoryToRemove"
			:entity-name="categoryToRemove.categoryName"
			entity-type="Service Category"
			@close="categoryToRemove = null"
			@delete="deleteCategory"
		/>
	</div>
</template>

<script>
import { mapGetters } from 'vuex'
import FilterBar from '../../../../../../../shared/components/filter-bar.vue'
import PrimaryActionButton from '../../../../../../../shared/components/primary-action-button.vue'
import SectionCard from '../../../../../../../shared/components/section-card.vue'
import DataTable from '../../../../../../../shared/components/data-table.vue'
import Dropdown from '../../../../../../../shared/components/dropdown.vue'
import CookieCategoryModal from './cookie-category-modal.vue'
import ConfirmDeleteModal from '../../../../../../../shared/components/confirm-delete-modal.vue'
import Chip from '../../../../../../../shared/components/chip.vue'
import { useLabels } from '../../../../../../../shared/state/admin-portal-navigation.js'
import { CAN_CREATE_UPDATE_COOKIE_CATEGORIES, CAN_DELETE_COOKIE_CATEGORIES } from '../../../../../../../shared/permissions/admin-portal-permissions.js'
import { deleteCookieCategory } from '../../../../../../../shared/utils/api/cookies/cookie-categories.js'
import { cookieCategorySearchQuery, changeCookieCategorySearchQuery, changeCookieCategoryType, selectedCookieCategoryType } from '../../../../../../../shared/state/cookie-categories.js'
import { selectedAdminPortalBrandId, changeAdminPortalBrandFilter, userHasBrand } from '../../../../../../../shared/state/brands.js'
const SYSTEM_CATEGORY_GUID = 'A8140E74-58E3-4DF7-B98B-19F8FAD43148'
export default {
	components: {
		DataTable,
		FilterBar,
		PrimaryActionButton,
		SectionCard,
		Dropdown,
		CookieCategoryModal,
		ConfirmDeleteModal,
		Chip
	},
	props: {
		cookieCategories: Array
	},
	setup () {
		return {
			userHasBrand,
			useLabels,
			cookieCategorySearchQuery,
			changeCookieCategorySearchQuery,
			changeCookieCategoryType,
			selectedCookieCategoryType,
			selectedAdminPortalBrandId,
			changeAdminPortalBrandFilter
		}
	},
	data () {
		return {
			showManageModal: false,
			cookieCategoryToEdit: null,
			categoryToRemove: null
		}
	},
	computed: {
		...mapGetters('auth', ['productAreaPermission']),
		categoryTypeFilterItems () {
			return [
				{ text: 'View All', value: null },
				{ text: 'Strictly Necessary', value: true },
				{ text: 'User Consent', value: false }
			]
		},
		tableHeaders () {
			return [
				{ value: 'id', text: 'ID', width: '6%' },
				{ value: 'brandName', text: useLabels('Brand'), width: '16%' },
				{ value: 'categoryName', text: 'Service Category Name', width: '18%' },
				{ value: 'type', text: 'Service Category Type', width: '18%', sortable: false },
				{ value: 'usage', text: 'Usage', width: '24%', sortable: false },
				{ value: 'createdDate', text: 'Created Date', width: '18%' },
				{ value: 'action', text: 'Action', width: '80px', align: 'center', sortable: false }
			]
		},
		filteredCategories () {
			return this.cookieCategories.filter(({ categoryName, brandId, isStrictlyCategory }) => {
				let check = true
				if (cookieCategorySearchQuery.value) check = categoryName.toLowerCase().includes(cookieCategorySearchQuery.value.toLowerCase())
				if (selectedAdminPortalBrandId.value !== null) check = check && brandId === selectedAdminPortalBrandId.value
				if (selectedCookieCategoryType.value !== null) check = check && isStrictlyCategory === selectedCookieCategoryType.value
				return check
			})
		},
		userCanCreateUpdate () {
			return this.productAreaPermission(CAN_CREATE_UPDATE_COOKIE_CATEGORIES)
		},
		userCanDelete () {
			return this.productAreaPermission(CAN_DELETE_COOKIE_CATEGORIES)
		}
	},
	methods: {
		canDeleteCategory (category) {
			return (
				this.userCanDelete &&
				category.countOfAssociatedCookieBanners === 0 &&
				category.countOfAssociatedCookieScripts === 0 &&
				category.isStrictlyCategory === false &&
				this.userHasBrand(category.brandId) &&
				!category.channelGuid
			)
		},
		isSystemCategory (item) {
			return item.channelGuid?.toUpperCase() === SYSTEM_CATEGORY_GUID
		},
		returnUsageCount (category) {
			return `${category.countOfAssociatedCookieBanners} Consent Banner${category.countOfAssociatedCookieBanners === 1 ? '' : 's'}
			/ ${category.countOfAssociatedCookieScripts} Service${category.countOfAssociatedCookieScripts === 1 ? '' : 's'}`
		},
		clearFilters () {
			selectedCookieCategoryType.value = null
		},
		closeModal () {
			this.showManageModal = false
			this.cookieCategoryToEdit = null
		},
		submitCookieCategory () {
			this.closeModal()
			this.$emit('load')
		},
		async deleteCategory () {
			await deleteCookieCategory(this.categoryToRemove.id)
			this.categoryToRemove = null
			this.$emit('load')
		}
	}
}
</script>
